import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { panelApi, type MetricSource, type SourceStatus } from '../../api/panel'
import { Spinner } from '../Spinner'
import { fmtDate, fmtDay } from './format'

// Configurações → "Painel — dados diários": até onde cada cliente ativo carregou e o botão
// que dispara a carga do histórico (13 meses, em segundo plano).

const SOURCES: MetricSource[] = ['google_ads', 'meta_ads', 'ga4']
const SOURCE_LABEL: Record<MetricSource, string> = { google_ads: 'Google', meta_ads: 'Meta', ga4: 'GA4' }

function SourceCell({ s }: { s: SourceStatus | undefined }) {
  if (!s) return <td className="px-2 py-1.5 text-slate-300">—</td>
  return (
    <td className="px-2 py-1.5" title={s.lastError ?? undefined}>
      {s.loadedFrom && s.loadedUntil ? (
        <span className={s.complete ? 'text-emerald-700' : 'text-slate-700'}>
          {fmtDate(s.loadedFrom)} a {fmtDay(s.loadedUntil)}
          {s.complete && ' ✓'}
        </span>
      ) : (
        <span className="text-slate-400">sem dados</span>
      )}
      {s.lastError && <span className="ml-1 text-rose-700">· erro</span>}
    </td>
  )
}

export function DailyMetricsSettings() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['daily-metrics-overview'],
    queryFn: () => panelApi.overview(),
    refetchInterval: (q) => (q.state.data?.historyLoading ? 15_000 : false),
  })
  const load = useMutation({
    mutationFn: () => panelApi.loadHistory(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['daily-metrics-overview'] }),
  })

  const done = data?.clients.filter((c) => c.sources.length && c.sources.every((s) => s.complete)).length ?? 0
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5">
      <h2 className="mb-1 text-base font-semibold text-slate-900">Painel — dados diários</h2>
      <p className="mb-3 text-sm text-slate-500">
        Uma linha por campanha por dia, só de clientes ativos. Todo dia às 02:00 (Brasília) os últimos 30 dias são
        refeitos; o histórico de 13 meses (desde {data ? fmtDate(data.historyStart) : '…'}) carrega em segundo plano
        depois da rodada, retomando de onde parou.
      </p>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => load.mutate()}
          disabled={load.isPending || data?.historyLoading}
          className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
        >
          {(load.isPending || data?.historyLoading) && <Spinner className="h-4 w-4 text-white" />}
          {data?.historyLoading ? 'Carregando histórico…' : 'Carregar histórico agora'}
        </button>
        {data && (
          <span className="text-sm text-slate-500">
            {done} de {data.clients.length} cliente(s) com histórico completo
          </span>
        )}
        {load.data && !load.data.started && <span className="text-sm text-amber-700">A carga já estava em andamento.</span>}
      </div>
      {isLoading && <Spinner />}
      {data && (
        <div className="max-h-96 overflow-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 border-b border-slate-200 bg-white text-slate-500">
              <tr>
                <th className="px-2 py-1.5 text-left font-medium">Cliente</th>
                {SOURCES.map((s) => (
                  <th key={s} className="px-2 py-1.5 text-left font-medium">
                    {SOURCE_LABEL[s]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.clients.map((c) => (
                <tr key={c.id} className="border-b border-slate-100">
                  <td className="px-2 py-1.5 text-slate-800">{c.name}</td>
                  {SOURCES.map((s) => (
                    <SourceCell key={s} s={c.sources.find((x) => x.source === s)} />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
